/**
 * PSSASMM Official Written Examination (Ujian Bertulis) Objective Questions Dataset
 * 
 * Total Bank: 115 Soalan Objektif
 * Allocation: 75 Minit (4,500 saat) setiap percubaan
 * Selection: 50 Soalan dipilih secara rawak bagi setiap percubaan
 * Limit: 2 Kali Percubaan
 * 
 * Domain Categories:
 * 1. Sahsiah & Adab Kependekaran (50 Soalan: ID 1 - 50)
 * 2. Silat & Kemahiran Silibus (30 Soalan: ID 51 - 80)
 * 3. Sejarah & Falsafah ASMM (35 Soalan: ID 81 - 115)
 *    (Sejarah Asal-Usul Patani, Srikandi Semerah, Pendekar Awang Sulung, Falsafah Biduk Berlalu Kiambang Bertaut,
 *     Pengasas Tuan Haji Shafie Pawang Diraja, Pewarisan 1969, Simpang Tiga Keladi 1983, Gelanggang Warisan,
 *     Makna 12 Lambang & Warna PSSASMM, Nilai Adab & Ukhuwah Murid)
 */

export interface UjianBertulisQuestion {
  id: number;
  category: 'Sahsiah & Adab Kependekaran' | 'Silat & Kemahiran Silibus' | 'Sejarah & Falsafah ASMM';
  question: string;
  options: [string, string, string, string]; // Exactly 4 options: A, B, C, D
  correctAnswer: number; // 0 = A, 1 = B, 2 = C, 3 = D
  explanation: string;
}

export const UJIAN_BERTULIS_QUESTIONS: UjianBertulisQuestion[] = [
  // =========================================================================
  // BAHAGIAN A: SAHSIAH & ADAB KEPENDEKARAN (SOALAN 1 - 50)
  // =========================================================================
  {
    id: 1,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah adab utama yang wajib disempurnakan oleh seorang pesilat sejurus melangkah masuk ke dalam gelanggang latihan?',
    options: [
      'Menanggalkan kasut, memberi salam kependekaran, dan menunduk hormat kepada gelanggang serta guru',
      'Terus melakukan regangan otot secara individu tanpa mempedulikan rakan lain',
      'Memakai senjata latihan dan memulakan libasan untuk memanaskan badan',
      'Mengambil gambar swafoto di sudut gelanggang untuk rekod kehadiran media sosial'
    ],
    correctAnswer: 0,
    explanation: 'Adab masuk ke gelanggang melambangkan kesucian niat, kerendahan hati, dan penghormatan kepada guru, gelanggang, serta warisan perguruan.'
  },
  {
    id: 2,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Pepatah Melayu "Ikut resmi padi, makin berisi makin tunduk" dalam sahsiah pesilat membawa maksud:',
    options: [
      'Pesilat perlu berpuasa dan menundukkan kepala sepanjang masa di tempat awam',
      'Semakin tinggi ilmu beladiri dan bengkung yang dicapai, semakin rendah diri dan berhemah pekertinya',
      'Pesilat dilarang memandang wajah lawan semasa bertempur',
      'Ilmu silat hanya sesuai dipelajari di kawasan sawah dan pedalaman'
    ],
    correctAnswer: 1,
    explanation: 'Ketinggian ilmu persilatan mestilah melahirkan sifat tawaduk, tidak menyombong diri, dan sentiasa menghormati masyarakat sekeliling.'
  },
  {
    id: 3,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Sekiranya seorang pesilat diasak dengan provokasi lisan atau ejekan oleh orang luar di tempat awam, apakah tindakan yang paling berhemah?',
    options: [
      'Membalas dengan jurus pukulan kencang untuk menunjukkan kehebatan silatnya',
      'Mengajak orang tersebut bertarung satu lawan satu di tempat sunyi',
      'Bertenang, mengawal emosi, beredar daripada persengketaan, dan mengelakkan sebarang kekerasan fizikal',
      'Memanggil seluruh ahli gelanggang untuk mengepung individu berkenaan'
    ],
    correctAnswer: 2,
    explanation: 'Prinsip kependekaran menegaskan bahawa ilmu silat adalah untuk beladiri dan keamanan, bukan untuk menunjuk-nunjuk atau mencetuskan permusuhan.'
  },
  {
    id: 4,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah pantang larang kependekaran yang paling berat dalam tradisi Seni Silat Awang Sulung Merah Muda?',
    options: [
      'Terlupa membawa botol minuman semasa sesi latihan',
      'Derhaka kepada ibu bapa dan guru, serta menggunakan ilmu silat untuk menindas orang yang lemah',
      'Memakai baju silat yang belum digosok licin',
      'Tidak dapat mengingati nama semua rakan segelanggang dalam masa seminggu'
    ],
    correctAnswer: 1,
    explanation: 'Kederhakaan kepada ibu bapa dan guru serta menzalimi makhluk lain membatalkan keberkatan ilmu dan melanggar sumpah pendekar.'
  },
  {
    id: 5,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Bagaimanakah pesilat harus menjaga batas pergaulan antara pesilat lelaki dan wanita di dalam mahupun di luar gelanggang?',
    options: [
      'Bebas bergaul tanpa sebarang batasan kerana semua dianggap adik-beradik silat',
      'Menjaga pandangan, mematuhi adab sopan susila Melayu Islam, dan memelihara batas sentuhan fizikal yang dilarang syarak',
      'Pesilat wanita dilarang sama sekali bercakap dengan pesilat lelaki walaupun untuk urusan latihan',
      'Hanya perlu menjaga batas semasa kehadiran jurulatih sahaja'
    ],
    correctAnswer: 1,
    explanation: 'Keperibadian pesilat berakar pada syariat dan adab susila murni Melayu Islam yang sentiasa memelihara batas pergaulan serta kehormatan diri.'
  },
  {
    id: 6,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah erti "Tawaduk" yang menjadi tiang seri sahsiah pesilat ASMM?',
    options: [
      'Rasa rendah hati, mengakui kelemahan diri di hadapan kebesaran Allah SWT, dan tidak memperlekehkan orang lain',
      'Ketakutan melampau ketika berhadapan dengan lawan di gelanggang',
      'Tidak mahu berlatih kerana merasakan diri sudah cukup mahir',
      'Menolak sebarang kenaikan bengkung kerana tidak mahu memikul tanggungjawab'
    ],
    correctAnswer: 0,
    explanation: 'Tawaduk adalah sifat mahmudah yang menjauhkan pesilat daripada sifat takbur, riyak, dan ujub terhadap kehandalan diri.'
  },
  {
    id: 7,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Mengapakah seorang pesilat wajib menghormati jurulatih dan guru silat walaupun di luar waktu latihan?',
    options: [
      'Kerana jurulatih berhak meminta wang saku daripada murid pada bila-bila masa',
      'Kerana guru adalah pewaris sanad keilmuan yang mencurahkan ilmu dan mendidik rohani pesilat dengan ikhlas',
      'Kerana murid wajib menjadi pembantu peribadi kepada jurulatih sepanjang hayat',
      'Hanya untuk mengelakkan diri daripada didenda semasa latihan seterusnya'
    ],
    correctAnswer: 1,
    explanation: 'Keberkatan ilmu persilatan bergantung kepada keredaan dan doa guru yang telah mencurahkan ilmu dengan penuh amanah.'
  },
  {
    id: 8,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah tanggungjawab seorang pesilat kanan terhadap pesilat baharu (adik segelanggang)?',
    options: [
      'Menyuruh pesilat baharu membelikan makanan dan minuman untuknya',
      'Membimbing dengan sabar, menunjukkan teladan adab yang baik, dan melindungi keselamatan mereka semasa latihan',
      'Menguji ketahanan fizikal mereka dengan pukulan keras tanpa belas kasihan',
      'Mengasingkan diri dan tidak bercampur dengan pesilat bengkung rendah'
    ],
    correctAnswer: 1,
    explanation: 'Pesilat kanan berperanan sebagai qudwah hasanah (contoh teladan) dan pembimbing yang memupuk rasa persaudaraan dan kasih sayang dalam gelanggang.'
  },
  {
    id: 9,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apabila berlaku perselisihan faham antara rakan segelanggang, langkah penyelesaian terbaik mengikut adab perguruan ialah:',
    options: [
      'Bertarung di belakang gelanggang tanpa pengetahuan sesiapa untuk menentukan siapa yang betul',
      'Membawa perkara tersebut ke media sosial untuk mendapatkan sokongan umum',
      'Berbincang secara matang berlandaskan semangat "Biduk Berlalu, Kiambang Bertaut" atau merujuk kepada jurulatih untuk perdamaian',
      'Memulaukan rakan tersebut daripada semua aktiviti persatuan'
    ],
    correctAnswer: 2,
    explanation: 'Semangat perpaduan dan ukhuwah ASMM mendidik pesilat memaafkan kesilapan dan menyelesaikan salah faham secara beradab dan berhemah.'
  },
  {
    id: 10,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Konsep "Pendekar Sejati" dalam falsafah Seni Silat Awang Sulung Merah Muda ditakrifkan sebagai:',
    options: [
      'Seseorang yang tidak pernah kalah dalam mana-mana pertandingan sabung fizikal',
      'Insan yang berani mempertahankan kebenaran, teguh berakhlak mulia, berjiwa kental, dan suka menolong mereka yang tertindas',
      'Seseorang yang memiliki senjata tajam paling banyak dan berbahaya',
      'Individu yang menuntut bayaran tinggi untuk melindungi kawasan perumahan'
    ],
    correctAnswer: 1,
    explanation: 'Darjat kependekaran bukan diukur pada kehandalan bertempur semata-mata, sebaliknya pada kemurnian akhlak, keberanian, dan kebajikan kepada masyarakat.'
  },
  {
    id: 11,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah tindakan wajar sebelum seseorang pesilat meninggalkan kawasan gelanggang setelah selesai sesi latihan?',
    options: [
      'Terus berlari keluar tanpa memberitahu sesiapa kerana terburu-buru',
      'Membuat bising dan ketawa terbahak-bahak di pintu keluar',
      'Bersalaman memohon izin daripada jurulatih serta rakan, dan memastikan kawasan gelanggang bersih dan teratur',
      'Meninggalkan peralatan latihan bertaburan di atas matras gelanggang'
    ],
    correctAnswer: 2,
    explanation: 'Adab penamat sesi latihan merangkumi tanda hormat berpamitan, bersalaman persaudaraan, dan memastikan gelanggang sentiasa bersih dan rapi.'
  },
  {
    id: 12,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Dalam Seni Silat Awang Sulung Merah Muda, ikrar pesilat bertujuan untuk menanamkan:',
    options: [
      'Perasaan megah dan rasa diri lebih hebat daripada perguruan silat lain',
      'Kesetiaan kepada agama, bangsa, raja, tanah air, guru, dan persatuan silat yang sah',
      'Keazaman untuk menewaskan semua ahli seni beladiri asing',
      'Matlamat untuk menjadi terkenal di peringkat antarabangsa'
    ],
    correctAnswer: 1,
    explanation: 'Ikrar pesilat memperkukuh benteng rohani dan kesetiaan tidak berbelah bahagi kepada nilai-nilai luhur agama, tanah air, dan perguruan.'
  },
  {
    id: 13,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Mengapakah seorang pesilat dilarang mempamerkan atau mendemonstrasikan buah-buah pukulan rahsia di khalayak umum secara semberono?',
    options: [
      'Kerana jurus tersebut boleh mendatangkan bahaya sekiranya ditiru secara salah dan mencemar maruah rahsia ilmu perguruan',
      'Kerana persatuan akan mengenakan denda wang ringgit kepada penonton',
      'Kerana jurus tersebut belum dipatenkan di bawah undang-undang cipta',
      'Kerana pesilat hanya boleh menunjukkan kemahiran apabila dibayar elaun'
    ],
    correctAnswer: 0,
    explanation: 'Ilmu pukulan dan kuncian adalah amanah perguruan; menunjukkannya tanpa kawalan boleh membawa fitnah, kecederaan, dan kehilangan maruah ilmu.'
  },
  {
    id: 14,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah sikap yang perlu ditunjukkan oleh seorang pesilat apabila tewas dalam sesuatu perlawanan atau ujian kenaikan bengkung?',
    options: [
      'Menyalahkan pengadil dan merosakkan kelengkapan dewan ujian',
      'Menerima keputusan dengan hati terbuka, muhasabah kelemahan diri, dan bertekad berlatih lebih gigih',
      'Menyatakan hasrat untuk berhenti daripada seni silat selama-lamanya',
      'Menghina peserta lain yang berjaya lulus dalam ujian berkenaan'
    ],
    correctAnswer: 1,
    explanation: 'Jiwa pendekar sejati tidak kenal putus asa; kekalahan adalah cermin untuk mempertingkatkan mutu latihan dan ketahanan mental.'
  },
  {
    id: 15,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah peranan ketepatan masa (disiplin masa) dalam kehidupan seorang ahli pesilat ASMM?',
    options: [
      'Ketepatan masa hanya penting semasa ujian penilaian bengkung dijalankan',
      'Mencerminkan komitmen, amanah diri, rasa hormat kepada masa jurulatih dan rakan latihan',
      'Pesilat bebas hadir pada bila-bila masa kerana silat adalah aktiviti sukarela',
      'Masa tidak mempunyai kaitan dengan pembinaan sahsiah pesilat'
    ],
    correctAnswer: 1,
    explanation: 'Disiplin masa adalah asas pembinaan jati diri; pendekar yang hebat bermula daripada pengurusan masa yang teliti dan berdisiplin.'
  },
  {
    id: 16,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Semasa menghadiri majlis rasmi perguruan, adab duduk yang paling sopan bagi pesilat Melayu di atas tikar atau permaidani ialah:',
    options: [
      'Duduk bersila dengan tegak dan tenang bagi lelaki, serta bersimpuh bagi wanita',
      'Duduk mencangkung sambil bermain telefon pintar',
      'Melunjurkan kedua-dua kaki tepat ke arah pentas utama atau guru',
      'Baring meniarap di bahagian belakang dewan majlis'
    ],
    correctAnswer: 0,
    explanation: 'Duduk bersila tegak (lelaki) dan bersimpuh rapi (wanita) melambangkan kesopanan tatasusila adab resam Melayu dalam majlis kependekaran.'
  },
  {
    id: 17,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Sekiranya pesilat melihat rakan segelanggang melakukan kesalahan tatatertib atau keselamatan semasa latihan, langkah pertama yang wajar ialah:',
    options: [
      'Menegur secara baik dan sopan di tempat itu atau memaklumkan kepada jurulatih bertugas',
      'Menertawakan rakan tersebut beramai-ramai supaya dia berasa malu',
      'Mengambil rakaman video dan menyebarkannya di platform media sosial',
      'Membiarkan sahaja kerana keselamatan orang lain bukan urusan peribadi'
    ],
    correctAnswer: 0,
    explanation: 'Semangat persaudaraan menuntut saling nasihat-menasihati demi keselamatan bersama dan menjaga tatatertib gelanggang.'
  },
  {
    id: 18,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah maksud falsafah kependekaran "Ilmu penyuluh dada, bukan senjata membinasa"?',
    options: [
      'Pesilat perlu membawa lampu suluh ke mana sahaja dia pergi',
      'Ilmu silat dituntut untuk mencerahkan hati, memperkukuh iman dan budi, bukan alat untuk memusnahkan sesama manusia',
      'Pesilat tidak dibenarkan mempelajari teknik pertahanan fizikal',
      'Semua ilmu silat wajib ditulis di atas kertas dan disimpan dalam peti rahsia'
    ],
    correctAnswer: 1,
    explanation: 'Ilmu silat adalah jalan penyucian jiwa, pembinaan jati diri, dan pembelaan keadilan, bukan alat permusuhan atau kehancuran.'
  },
  {
    id: 19,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah tujuan utama amalan bersalaman dan menundukkan sedikit kepala semasa memberi salam kependekaran?',
    options: [
      'Memeriksa sama ada tangan rakan berpeluh atau kering',
      'Menghapuskan rasa keegoan dalam diri, merapatkan silaturahim, dan mendoakan kesejahteraan sesama saudara seperguruan',
      'Menguji kekuatan genggaman tangan lawan secara senyap',
      'Syarat wajib untuk melayakkan diri mendapat minuman percuma'
    ],
    correctAnswer: 1,
    explanation: 'Salam kependekaran melambangkan pembersihan batiniah, merendahkan ego peribadi, dan memancarkan kasih sayang ukhuwah.'
  },
  {
    id: 20,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Bagaimanakah pesilat harus bertindak sekiranya ibu bapa meminta bantuan di rumah sedangkan masa latihan silat hampir bermula?',
    options: [
      'Mengabaikan panggilan ibu bapa dan terus meluru ke gelanggang silat',
      'Mendahulukan bakti kepada ibu bapa dengan menyempurnakan bantuan terlebih dahulu, kemudian memohon izin jurulatih sekiranya terlewat',
      'Memarahi ibu bapa kerana mengganggu jadual aktiviti kependekaran',
      'Berhenti daripada persatuan silat serta-merta'
    ],
    correctAnswer: 1,
    explanation: 'Ketaatan dan khidmat bakti kepada ibu bapa adalah teras keberkatan hidup seorang pesilat; tanpa reda ibu bapa, ilmu tiada nilainya.'
  },
  {
    id: 21,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Mengapakah seorang pesilat dilarang sama sekali menyertai aktiviti kongsi gelap atau kumpulan samseng jalanan?',
    options: [
      'Kerana bayaran yuran kongsi gelap terlalu tinggi',
      'Kerana perbuatan tersebut mencemarkan maruah perguruan, membatalkan ikrar kependekaran, dan menyalahi undang-undang negara',
      'Kerana persatuan silat mahu memonopoli aktiviti samseng di kawasan berkenaan',
      'Hanya kerana pakaian kongsi gelap tidak sama dengan busana silat'
    ],
    correctAnswer: 1,
    explanation: 'Silat ASMM mendidik insan menjadi warganegara berintegriti dan pelindung keamanan, bukan pemusnah ketenteraman awam.'
  },
  {
    id: 22,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah maksud adab "Menjaga Nama Baik Guru & Perguruan"?',
    options: [
      'Mencetak poster gambar guru dan menampalnya di tiang-tiang lampu',
      'Menjaga tingkah laku peribadi walau di mana berada agar tidak mencetuskan fitnah atau cemuhan terhadap guru dan persatuan',
      'Menghantar hadiah mewah kepada guru setiap minggu',
      'Memaksa semua ahli keluarga menyertai gelanggang silat yang sama'
    ],
    correctAnswer: 1,
    explanation: 'Kemuliaan perguruan dicerminkan oleh keperibadian muridnya; sebarang perlakuan sumbang murid akan mencemarkan maruah perguruan.'
  },
  {
    id: 23,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Dalam situasi kecemasan di tempat awam di mana seorang wanita diragut beg tangannya, tindakan paling tepat bagi seorang pesilat ialah:',
    options: [
      'Hanya memerhati dan merakam kejadian untuk tontonan media sosial',
      'Membantu mangsa dengan berani dan berhemah, bertindak mengikut kemampuan diri, dan menghubungi pihak polis segera',
      'Melarikan diri kerana tidak mahu terbabit dalam urusan orang lain',
      'Menuntut ganjaran wang tunai daripada mangsa sebelum menghulurkan bantuan'
    ],
    correctAnswer: 1,
    explanation: 'Keberanian pendekar adalah untuk membela keadilan dan melindungi golongan yang dizalimi berlandaskan hikmah dan undang-undang.'
  },
  {
    id: 24,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah hikmah di sebalik peraturan mewajibkan pakaian seragam (busana silat lengkap berbengkung) semasa sesi latihan?',
    options: [
      'Menunjukkan kekayaan ahli gelanggang kepada orang awam',
      'Memupuk keseragaman, disiplin mental, kesaksamaan darjat di gelanggang, dan rasa bangga terhadap warisan Melayu',
      'Menyukarkan pergerakan fizikal supaya latihan terasa lebih berat',
      'Hanya untuk memudahkan jurulatih mengambil gambar aktiviti'
    ],
    correctAnswer: 1,
    explanation: 'Busana silat menyatukan darjat semua pesilat di atas matras latihan; tiada yang kaya atau miskin, semua sama di sisi adab perguruan.'
  },
  {
    id: 25,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah adab pesilat terhadap senjata tradisional warisan seperti keris, tekpi, atau parang lading?',
    options: [
      'Menganggap senjata sebagai alat permainan untuk menakut-nakutkan kanak-kanak',
      'Menghormati senjata sebagai amanah seni mempertahankan diri, menjaganya dengan cermat, dan tidak menghunus tanpa sebab syarie',
      'Membawanya ke pusat beli-belah untuk menunjukkan kegagahan diri',
      'Membiarkan senjata berkarat di sudut rumah tanpa penjagaan'
    ],
    correctAnswer: 1,
    explanation: 'Senjata tradisional dipandang mulia sebagai lambang daulat bangsa dan maruah; ia tidak sesekali dihunus melainkan pada jalan kebenaran.'
  },
  {
    id: 26,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Sekiranya seorang pesilat dinaikkan bengkung atau dianugerahkan cula baharu, reaksi hati yang paling terpuji ialah:',
    options: [
      'Berasa diri lebih mulia dan memandang rendah rakan-rakan yang belum dinaikkan bengkung',
      'Bersyukur kepada Allah SWT, berterima kasih kepada guru, dan menyedari bertambahnya beban amanah yang dipikul',
      'Menuntut layanan istimewa daripada pengurusan gelanggang',
      'Meminta bayaran yuran bulanan dimansuhkan serta-merta'
    ],
    correctAnswer: 1,
    explanation: 'Setiap peningkatan bengkung adalah penambahan tanggungjawab memelihara silibus, membimbing adik-adik, dan mempamerkan akhlak luhur.'
  },
  {
    id: 27,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah kesan buruk sekiranya pesilat mempelajari ilmu beladiri dengan niat untuk membalas dendam atau membalas sakit hati lama?',
    options: [
      'Ilmunya akan menjadi semakin berbisa dan ditakuti musuh',
      'Hatinya akan menjadi gelap, hilang keberkatan ilmu, mudah tewas akibat emosi yang tidak stabil, dan merosakkan jiwa sendiri',
      'Dia akan dinaikkan bengkung dengan lebih pantas oleh dewan penguji',
      'Tiada sebarang kesan kerana niat tidak mempengaruhi kemahiran fizikal'
    ],
    correctAnswer: 1,
    explanation: 'Niat yang kotor menghapuskan nur ilmu persilatan dan membinasakan pelakunya; silat menuntut kesucian hati seputih kain kafan.'
  },
  {
    id: 28,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah peranan amalan membaca doa, selawat ke atas Nabi SAW, dan surah Al-Fatihah sebelum memulakan sesi latihan di gelanggang?',
    options: [
      'Sekadar mengisi masa lapang sementara menunggu kehadiran semua ahli',
      'Memohon perlindungan daripada Allah SWT daripada sebarang kecederaan, menyucikan niat, dan menyambung sanad keberkatan rohani',
      'Tradisi lama yang tidak mempunyai sebarang kaitan dengan keselamatan pesilat',
      'Hanya dilakukan sekiranya terdapat tetamu kehormat hadir'
    ],
    correctAnswer: 1,
    explanation: 'Latihan silat Melayu Islam bertunjangkan tauhid dan tawakal kepada Allah SWT demi keselamatan fizikal dan kejernihan rohani.'
  },
  {
    id: 29,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Bagaimanakah pesilat harus menjaga kebersihan fizikal dan pakaian sebelum melangkah ke gelanggang?',
    options: [
      'Cukup sekadar memakai minyak wangi tanpa perlu mandi atau membasuh baju',
      'Memastikan tubuh badan bersih, kuku dipotong pendek dan kemas, pakaian seragam bersih, serta tidak berbau yang mengganggu rakan lain',
      'Kebersihan tidak penting kerana latihan silat sememangnya mengeluarkan peluh kotor',
      'Memakai pakaian sukan yang koyak asalkan selesa'
    ],
    correctAnswer: 1,
    explanation: 'Kebersihan adalah sebahagian daripada iman; kuku pendek mengelakkan kecederaan calar pada rakan semasa teknik kuncian.'
  },
  {
    id: 30,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah tindakan paling mulia seorang pesilat apabila menerima pujian melambung daripada orang ramai berkenaan kehebatannya?',
    options: [
      'Mendabik dada dan mengakui bahawa dirinya sememangnya tiada tandingan di daerah tersebut',
      'Mengembalikan segala pujian kepada Allah SWT (Alhamdulillah) dan mengingati bahawa segala kekuatan adalah pinjaman semata-mata',
      'Mencabar orang yang memuji untuk beradu tenaga dengannya',
      'Menuntut hadiah penghargaan wang tunai atas pujian tersebut'
    ],
    correctAnswer: 1,
    explanation: 'Pesilat mukmin sentiasa sedar bahawa daya dan kekuatan (La hawla wa la quwwata illa billah) hanya milik mutlak Allah SWT.'
  },
  {
    id: 31,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Dalam adab perguruan ASMM, mengapakah pesilat dilarang melangkah atau memijak senjata latihan rakan atau gelanggang?',
    options: [
      'Kerana senjata latihan diperbuat daripada kaca yang mudah pecah',
      'Sebagai tanda memuliakan peralatan ilmu dan melatih diri sentiasa berwaspada terhadap keselamatan dan adab ketertiban',
      'Kerana senjata latihan berharga ribuan ringgit',
      'Hanya kerana takut kasut menjadi kotor'
    ],
    correctAnswer: 1,
    explanation: 'Adab terhadap alat keilmuan mencerminkan kehalusan budi pekerti pesilat dalam menghargai setiap wasilah pembelajaran.'
  },
  {
    id: 32,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah erti peribahasa kependekaran "Keris tersisip di pinggang bukan untuk menikam kawan, tapi menjaga amanah watan"?',
    options: [
      'Pesilat perlu sentiasa membawa keris ke pejabat setiap hari',
      'Ilmu dan kekuatan yang dimiliki adalah untuk membela keadilan dan tanah air, bukan untuk bermusuhan sesama sendiri',
      'Pesilat dilarang memiliki kawan semasa latihan silat',
      'Keris hanya boleh digunakan oleh golongan tertentu sahaja'
    ],
    correctAnswer: 1,
    explanation: 'Persaudaraan sesama anak bangsa diutamakan; kekuatan persilatan disiapsiagakan untuk mempertahankan kedaulatan tanah air.'
  },
  {
    id: 33,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apabila jurulatih sedang memberikan penerangan teori atau demonstrasi buah silat, adab murid yang betul ialah:',
    options: [
      'Bercakap-cakap dengan rakan di sebelah dan ketawa kecil',
      'Memberi tumpuan penuh dengan pandangan mata, memasang telinga dengan hening, dan tidak mencelah tanpa izin',
      'Mencuba jurus lain di sudut belakang gelanggang tanpa menghiraukan guru',
      'Bermain telefon bimbit secara sembunyi di belakang barisan'
    ],
    correctAnswer: 1,
    explanation: 'Hening dan fokus semasa guru mengajar adalah kunci menyerap rahsia gerak dan mengelakkan kesilapan teknikal yang berbahaya.'
  },
  {
    id: 34,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah sikap pesilat ASMM terhadap pesilat daripada aliran atau perguruan silat lain di Malaysia?',
    options: [
      'Memandang rendah dan menganggap aliran lain tidak mempunyai buah pukulan yang mantap',
      'Menghormati kepelbagaian aliran, mengeratkan ukhuwah sesama rumpun persilatan Melayu, dan saling bertukar pandangan secara berhemah',
      'Mencabar mereka bertarung untuk membuktikan perguruan mana paling unggul',
      'Memutuskan hubungan persahabatan dengan rakan yang berlainan perguruan'
    ],
    correctAnswer: 1,
    explanation: 'Semua perguruan silat Melayu adalah serumpun warisan nenek moyang; menghormati perguruan lain adalah sebahagian daripada adab pendekar.'
  },
  {
    id: 35,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah kayu ukur paling hakiki bahawa seseorang pesilat telah berjaya menghayati falsafah Seni Silat Awang Sulung Merah Muda?',
    options: [
      'Berapa banyak piala dan pingat emas yang berjaya dimenanginya di kejohanan sukan',
      'Perubahan akhlaknya menjadi insan yang semakin penyayang, bertanggungjawab, berdisiplin, berani kerana benar, dan tunduk merendah diri',
      'Keupayaannya mematahkan kayu tebal dengan menggunakan kepala',
      'Bilangan pengikut di media sosial yang meminati aksi silatnya'
    ],
    correctAnswer: 1,
    explanation: 'Kejayaan sejati silat tradisi bukan pada fizikal semata-mata, tetapi pada transformasi rohani dan kemuliaan adab dalam kehidupan seharian.'
  },
  {
    id: 36,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah adab yang paling utama apabila pesilat ASMM dijemput mempersembahkan silat pengantin atau silat pulut dalam majlis perkahwinan masyarakat?',
    options: [
      'Menuntut bayaran saguhati yang tinggi sebelum mahu melangkah masuk ke halaman majlis',
      'Menjaga kesantunan gerak tari, menghormati pasangan pengantin dan tetamu, serta mempersembahkan seni warisan dengan niat meraikan dan mendoakan keberkatan',
      'Menunjukkan buah pukulan maut yang menakutkan para jemputan dan kanak-kanak',
      'Mencabar pesilat dari kumpulan gendang pengantin lain untuk bertarung di hadapan pelamin'
    ],
    correctAnswer: 1,
    explanation: 'Silat pengantin dan keramaian adalah medan mempamerkan keindahan seni budi Melayu, meraikan tuan rumah, dan mengeratkan silaturahim masyarakat secara berhemah.'
  },
  {
    id: 37,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Dalam era digital kini, bagaimanakah etika seorang pesilat ASMM semasa memuat naik rakaman video atau status di media sosial (TikTok, Facebook, Instagram)?',
    options: [
      'Memelihara kerahsiaan ilmu perguruan, tidak mencetuskan provokasi terhadap aliran silat lain, dan menampilkan imej pendekar yang berakhlak mulia',
      'Mencabar mana-mana guru silat lain secara terbuka di ruangan komen untuk menarik tontonan (views)',
      'Menyiarkan buah pukulan rahsia dan kuncian maut perguruan tanpa kebenaran guru demi mengejar populariti',
      'Menggunakan bahasa kesat dan mengejek pesilat bengkung rendah di media sosial'
    ],
    correctAnswer: 0,
    explanation: 'Pesilat bertanggungjawab menjaga maruah perguruan di alam maya; media sosial hendaklah dijadikan wahana dakwah dan penyebaran nilai murni kependekaran.'
  },
  {
    id: 38,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah amanah yang terkandung di sebalik pemakaian dan pemeliharaan Selendang Kebesaran Perguruan ASMM?',
    options: [
      'Sebagai kain alas tidur apabila kepenatan selepas sesi latihan',
      'Alat permainan untuk ditarik-tarik bersama rakan semasa waktu rehat',
      'Melambangkan amanah bai\'ah perjuangan, pemelihara maruah keilmuan di bahu pesilat, dan wajib diletakkan di tempat yang bersih dan mulia',
      'Hiasan fesyen semata-mata untuk menarik perhatian masyarakat'
    ],
    correctAnswer: 2,
    explanation: 'Selendang kebesaran melambangkan amanah perguruan yang dipikul di bahu; ia melambangkan kesucian niat jihad mempertahankan maruah bangsa dan agama.'
  },
  {
    id: 39,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Mengapakah sifat "Sabar" dan kemampuan menahan rasa marah amat dituntut dalam sahsiah seorang pendekar ASMM?',
    options: [
      'Supaya pesilat kelihatan lemah dan mudah diperkotak-katikkan oleh pihak lawan',
      'Kerana kemarahan yang tidak terkawal boleh mematikan pertimbangan akal waras, membawa kecederaan zalim, dan membinasakan diri sendiri',
      'Hanya kerana peraturan ujian bengkung melarang pesilat tersenyum',
      'Sifat sabar hanya perlu diamalkan ketika berada di rumah sahaja'
    ],
    correctAnswer: 1,
    explanation: 'Nabi SAW berpesan bahawa orang yang kuat bukanlah orang yang menang dalam pergelutan, tetapi orang yang mampu mengawal nafsunya ketika marah.'
  },
  {
    id: 40,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apabila seorang pesilat mendapati ada kemusykilan mengenai sesuatu buah pukulan atau falsafah perguruan, apakah adab menuntut ilmu yang paling berhemah?',
    options: [
      'Memotong perbualan guru secara tiba-tiba di tengah-tengah sesi penerangan umum',
      'Mencari masa yang sesuai, memohon izin dengan sopan dan merendah diri, lalu mengemukakan pertanyaan dengan penuh tertib',
      'Mempersendakan teknik yang diajar oleh guru di hadapan murid-murid baharu',
      'Menganggap soalan itu tidak penting dan mereka-reka jawapan sendiri tanpa bimbingan guru'
    ],
    correctAnswer: 1,
    explanation: 'Adab bertanya dengan sopan pada waktu yang tepat adalah kunci terbukanya pintu kefahaman dan keberkatan ilmu daripada sang guru.'
  },
  {
    id: 41,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Pepatah persilatan "Pantang Keris Sama Sarung Bergeser Bilah" memberi peringatan tegas kepada seluruh pesilat ASMM tentang larangan:',
    options: [
      'Memakai sarung keris yang diperbuat daripada kayu keras',
      'Menyimpan dua bilah keris dalam satu beg latihan',
      'Berbalah, berpecah belah, dan bermusuhan sesama saudara seperguruan yang bernaung di bawah satu panji',
      'Membeli senjata tradisional daripada pandai besi yang sama'
    ],
    correctAnswer: 2,
    explanation: 'Pantang keris sama sarung menuntut kesetiaan dan keharmonian mutlak sesama ahli keluarga silat; sebarang perbalahan dalaman meruntuhkan daulat persatuan.'
  },
  {
    id: 42,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Bagaimanakah pesilat harus melayan dan menguruskan peralatan latihan gelanggang (seperti punching pad, pedang kayu, dan cermin gelanggang)?',
    options: [
      'Menjaga dan menggunakannya dengan cermat seolah-olah harta sendiri, serta menyusun semula di tempat simpanan selepas selesai latihan',
      'Membaling dan mencampakkan peralatan ke lantai sebaik sahaja selesai sesi drills',
      'Membawa pulang peralatan gelanggang ke rumah tanpa memberitahu sesiapa',
      'Membiarkan peralatan latihan terdedah di bawah hujan dan panas di luar gelanggang'
    ],
    correctAnswer: 0,
    explanation: 'Menjaga harta dan kemudahan gelanggang melatih sifat amanah, cermat, dan menghargai pengorbanan persatuan menyediakan kelengkapan latihan.'
  },
  {
    id: 43,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah peranan sahsiah pesilat ASMM terhadap golongan rentan dalam masyarakat seperti warga emas, orang kurang upaya (OKU), dan kanak-kanak?',
    options: [
      'Menghulurkan bantuan dengan penuh ihsan, melindungi keselamatan mereka, dan mengutamakan kebajikan mereka dalam urusan harian',
      'Mengabaikan mereka kerana silat hanya mementingkan orang yang kuat fizikalnya',
      'Menunjukkan gaya kekuda silat untuk menguji ketahanan mental mereka',
      'Menyuruh mereka membersihkan kawasan gelanggang silat'
    ],
    correctAnswer: 0,
    explanation: 'Teras kependekaran Melayu adalah berkhidmat dan menaungi mereka yang lemah; kekuatan jasmani pesilat disandarkan pada sifat rahmat dan ihsan.'
  },
  {
    id: 44,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Semasa latihan berpasangan (sparring/kuncian), sekiranya anda menyedari pasangan latihan anda melakukan kesilapan atau mempunyai kelemahan ketara, tindakan yang beradab ialah:',
    options: [
      'Mengejek dan mentertawakan kesilapannya di hadapan seluruh gelanggang',
      'Menceritakan kelemahan rakan tersebut di aplikasi sembang kumpulan WhatsApp',
      'Mengambil peluang untuk memukulnya sekuat hati bagi memenangi latihan',
      'Membimbing dan membetulkan kesilapannya secara baik, serta menjaga aibnya sebagai saudara seperguruan'
    ],
    correctAnswer: 3,
    explanation: 'Latihan berpasangan adalah medan tolong-menolong mempertingkatkan kemahiran bersama (ta\'awun), bukan untuk menjatuhkan maruah rakan.'
  },
  {
    id: 45,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Dalam etika kependekaran, apakah erti "Menepati Janji" apabila pesilat telah menyatakan persetujuan untuk hadir bertugas atau membantu aktiviti persatuan?',
    options: [
      'Janji boleh diabaikan sekiranya ada aktiviti santai lain yang lebih menyeronokkan',
      'Janji adalah amanah yang mencerminkan maruah dan integriti seorang pendekar; wajib ditepati melainkan atas keuzuran syarie yang dimaklumkan awal',
      'Pesilat hanya perlu menepati janji jika dijanjikan bayaran wang tunai',
      'Kehadiran adalah pilihan mutlak individu dan tidak melibatkan sebarang etika moral'
    ],
    correctAnswer: 1,
    explanation: 'Integriti dan sifat menepati janji adalah pakaian orang beriman dan pendekar sejati; mungkir janji tanpa sebab meruntuhkan kepercayaan guru dan rakan.'
  },
  {
    id: 46,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Sekiranya seseorang pesilat ASMM dinobatkan sebagai johan dalam sesuatu kejohanan silat, bagaimanakah cara terbaik menzahirkan rasa syukur?',
    options: [
      'Melompat-lompat sambil mengejek pihak lawan yang tewas sebagai tanda puas hati',
      'Mendakwa bahawa kejayaan itu adalah hasil kehebatan diri sendiri tanpa bantuan guru dan doa ibu bapa',
      'Melakukan sujud syukur, mengucapkan tahniah kepada pihak lawan atas saingan hebat, dan menghadiahkan kemenangan kepada guru dan ibu bapa',
      'Menolak untuk berjabat tangan dengan pengadil dan jurulatih pasukan lawan'
    ],
    correctAnswer: 2,
    explanation: 'Kemenangan hakiki dihiasi dengan kerendahan hati, sujud syukur kepada Allah SWT, dan menghormati lawan yang telah berjuang bersama di gelanggang.'
  },
  {
    id: 47,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Pepatah Melayu "Kerana pulut santan binasa, kerana mulut badan binasa" dalam adab kependekaran menekankan peri pentingnya:',
    options: [
      'Memakan pulut dengan santan pekat setiap hari sebelum latihan silat',
      'Menjaga pertuturan lidah daripada mencaci, menyindir, berbohong, atau memfitnah yang boleh mencetuskan perbalahan',
      'Menjual beras pulut di koperasi gelanggang persatuan',
      'Mengelakkan diri daripada bercakap dengan sesiapa pun sepanjang hayat'
    ],
    correctAnswer: 1,
    explanation: 'Lidah yang tidak bertulang lebih berbisa daripada mata keris; pendekar sejati sentiasa memelihara tutur kata agar manis didengar dan membawa damai.'
  },
  {
    id: 48,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apakah nilai murni yang dizahirkan apabila seorang pesilat secara sukarela menyumbangkan tenaga membantu mengecat atau membaiki kemudahan gelanggang tanpa meminta sebarang upah?',
    options: [
      'Keikhlasan berbakti dan rasa kepunyaan (sense of belonging) yang tinggi terhadap kelangsungan rumah pusaka perguruan',
      'Kerugian masa dan tenaga yang sia-sia tanpa pulangan kewangan',
      'Cara pantas untuk membeli bengkung baharu tanpa perlu menduduki ujian',
      'Hanya mahu membodek guru supaya disayangi'
    ],
    correctAnswer: 0,
    explanation: 'Khidmat sukarela (infaq tenaga) menyuburkan jiwa ikhlas dan rasa tanggungjawab memelihara gelanggang sebagai wadah ilmu warisan turun-temurun.'
  },
  {
    id: 49,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Apabila berziarah ke teratak kediaman Guru Utama atau jurulatih sempena hari raya atau majlis silaturahim, adab yang wajar diamalkan ialah:',
    options: [
      'Membawa buah tangan sekadar kemampuan, memberi salam dengan hening, duduk sopan, dan menjaga tatasusila adab tetamu Melayu',
      'Menerobos masuk ke bilik peribadi guru tanpa kebenaran',
      'Membuat bising dan mengkritik hidangan makanan yang disediakan',
      'Hanya berkunjung sekiranya guru menyediakan duit raya bernilai besar'
    ],
    correctAnswer: 0,
    explanation: 'Ziarah mahabbah ke rumah guru menuntut adab penghormatan tertinggi; ia merapatkan ikatan kasih sayang antara guru dan murid berlandaskan sunnah.'
  },
  {
    id: 50,
    category: 'Sahsiah & Adab Kependekaran',
    question: 'Mengapakah cogan kata "Bersatu Teguh, Bercerai Roboh" menjadi teras kekuatan perpaduan seluruh warga panji Seni Silat Awang Sulung Merah Muda?',
    options: [
      'Kerana persatuan silat bercadang menubuhkan parti politik tempatan',
      'Kerana perpecahan akan melemahkan daulat persatuan, manakala kebersamaan hati dan kesatuan saf pesilat menjamin kelestarian warisan pusaka bangsa',
      'Hanya slogan hiasan pada dinding dewan gelanggang',
      'Supaya pesilat boleh memaksa orang ramai menyertai persatuan'
    ],
    correctAnswer: 1,
    explanation: 'Kesatuan hati (ittihad) adalah benteng pertahanan persatuan; silat bukan sekadar gerak fizikal tetapi tali persaudaraan yang mengikat seluruh anak bangsa.'
  },

  // =========================================================================
  // BAHAGIAN B: SILAT, SILIBUS & KEMAHIRAN ASMM (SOALAN 51 - 80)
  // =========================================================================
  {
    id: 51,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam silibus asas Seni Silat Awang Sulung Merah Muda, apakah kedudukan kekuda "Separuh Tiang"?',
    options: [
      'Kedudukan berdiri tegak dengan kedua-dua belah kaki dirapatkan tanpa ruang',
      'Kedudukan lutut direndahkan separuh paras dengan bukaan kaki seimbang dan pusat graviti stabil',
      'Kedudukan melompat di udara dengan satu kaki terangkat tinggi',
      'Kedudukan duduk bersila sepenuhnya di atas lantai'
    ],
    correctAnswer: 1,
    explanation: 'Kekuda separuh tiang memberi asas imbangan kukuh, keanjalan pergerakan menyerang atau mengelak, serta penyerapan daya serangan lawan.'
  },
  {
    id: 52,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah perbezaan utama antara "Tumbuk Hadapan" dan "Tumbuk Bawah" dalam gerak asas ASMM?',
    options: [
      'Tumbuk Hadapan disasarkan ke bahagian dada/ulu hati, manakala Tumbuk Bawah menyasarkan kawasan abdomen/bawah pusat lawan',
      'Tumbuk Hadapan menggunakan tapak tangan terbuka, manakala Tumbuk Bawah menggunakan siku',
      'Tumbuk Hadapan hanya dilakukan sambil melompat, manakala Tumbuk Bawah dilakukan sambil baring',
      'Kedua-dua tumbukan adalah sama tanpa sebarang perbezaan sasaran'
    ],
    correctAnswer: 0,
    explanation: 'Tumbuk hadapan menyasarkan zon dada/muka mengikut garis lurus, manakala tumbuk bawah menyasarkan zon abdomen/bawah kekuda lawan.'
  },
  {
    id: 53,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah fungsi pergerakan "Elakan Luar" apabila lawan melepaskan serangan tumbukan lurus ke hadapan?',
    options: [
      'Melangkah masuk tepat ke garis tumbukan lawan untuk menahan dengan dahi',
      'Mengalihkan kedudukan badan ke arah luar garis serangan sambil mengekalkan imbangan untuk serangan balas',
      'Berundur ke belakang sejauh lima meter tanpa menoleh',
      'Menjatuhkan diri ke lantai secara sukarela'
    ],
    correctAnswer: 1,
    explanation: 'Elakan luar membolehkan pesilat keluar dari zon bahaya (blind spot lawan) dan membuka ruang serangan balas dengan mudah.'
  },
  {
    id: 54,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam teknik tendangan silat asas, "Tendang Sisi" memanfaatkan bahagian manakah untuk melancarkan impak sasaran?',
    options: [
      'Punggung kaki atau tumit dan sisi luar tapak kaki pesilat',
      'Lutut bahagian belakang sahaja',
      'Jari kelinking kaki semata-mata',
      'Tapak kaki bahagian tengah yang lembut'
    ],
    correctAnswer: 0,
    explanation: 'Tendang sisi menggunakan struktur padu tumit atau sisi luar kaki (pisau kaki) untuk menghasilkan daya hentakan yang mantap dan selamat bagi pesilat.'
  },
  {
    id: 55,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah turutan modul kenaikan bengkung asas dalam sistem perguruan PSSASMM?',
    options: [
      'Bengkung Hitam → Bengkung Hijau → Bengkung Biru',
      'Bengkung Putih → Bengkung Merah → Bengkung Kuning',
      'Bengkung Kuning → Bengkung Putih → Bengkung Merah',
      'Bengkung Biru → Bengkung Jingga → Bengkung Ungu'
    ],
    correctAnswer: 1,
    explanation: 'Sistem ASMM berakar pada Modul Bengkung Putih (peringkat pesilat), diikuti Modul Bengkung Merah (pembantu jurulatih), dan Bengkung Kuning (jurulatih/kepimpinan).'
  },
  {
    id: 56,
    category: 'Silat & Kemahiran Silibus',
    question: 'Seseorang yang memegang Bengkung Putih Peringkat 1 (Cula 1) membawa gelaran rasmi:',
    options: [
      'Pesilat Asas',
      'Pesilat Muda',
      'Pesilat Kanan',
      'Pesilat Sulung'
    ],
    correctAnswer: 1,
    explanation: 'Mengikut daftar bengkung rasmi ASMM: Putih 0 = Pesilat Asas, Putih 1 = Pesilat Muda, Putih 2 = Pesilat Kanan, Putih 3 = Pesilat Sulung.'
  },
  {
    id: 57,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah fungsi jalur Cula (Stripe) pada bengkung pesilat ASMM?',
    options: [
      'Hiasan jahitan semata-mata tanpa sebarang erti',
      'Penanda darjah kemajuan penguasaan silibus dan tingkatan pencapaian kependekaran pesilat',
      'Tempat untuk menggantung kunci gelanggang',
      'Penunjuk bilangan senjata yang dimiliki pesilat'
    ],
    correctAnswer: 1,
    explanation: 'Cula melambangkan anak tangga ilmu dan kelayakan penguasaan silibus yang diuji secara rasmi oleh dewan penguji persatuan.'
  },
  {
    id: 58,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah perbezaan konsep antara "Bunga Silat" (Asas Tari) dan "Buah Silat" (Buah Tempur)?',
    options: [
      'Bunga Silat adalah pergerakan estetik pembayang gerak dan perangkap, manakala Buah Silat adalah penyudah tempur yang berisi pukulan/kuncian',
      'Bunga Silat menggunakan senjata api, manakala Buah Silat menggunakan buah-buahan sebenar',
      'Bunga Silat hanya untuk kanak-kanak, manakala Buah Silat untuk orang dewasa',
      'Kedua-dua istilah membawa erti yang sama sepenuhnya'
    ],
    correctAnswer: 0,
    explanation: 'Bunga melambangkan kehalusan, kelicikan helah, dan pergerakan persediaan, manakala buah adalah gerak pantas yang melumpuhkan lawan.'
  },
  {
    id: 59,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam teknik asas tangkisan tangan, "Tangkis Luar" berfungsi untuk:',
    options: [
      'Menolak garis serangan tumbukan lurus lawan ke arah luar badan dengan menggunakan tulang pergelangan lengan bawah',
      'Menangkap kedua-dua belah kaki lawan serentak',
      'Menutup pandangan mata sendiri semasa diserang',
      'Menghayun tangan ke udara tanpa menyentuh serangan lawan'
    ],
    correctAnswer: 0,
    explanation: 'Tangkis luar memesongkan arah daya serangan lawan keluar dari garisan tengah tubuh pesilat.'
  },
  {
    id: 60,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah fungsi "Kuda-Kuda Depan" dalam pelaksanaan serangan jarak sederhana?',
    options: [
      'Memberi tekanan daya dorongan ke hadapan dengan kestabilan kaki hadapan yang dibengkokkan dan kaki belakang yang lurus',
      'Membolehkan pesilat tidur di atas lantai gelanggang',
      'Memudahkan pesilat berpaling dan melarikan diri',
      'Mengurangkan ketinggian badan pesilat kepada paras tanah sepenuhnya'
    ],
    correctAnswer: 0,
    explanation: 'Kuda-kuda depan menumpukan momentum ke arah sasaran serta mengunci tapak agar serangan mempunyai daya impak yang kukuh.'
  },
  {
    id: 61,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam silibus pertempuran tangan kosong ASMM, teknik "Kuncian Sendi" bertujuan untuk:',
    options: [
      'Mematahkan tulang lawan dengan sengaja agar lawan cacat seumur hidup',
      'Melumpuhkan pergerakan lawan dengan mengawal tuas anatomi sendi (pergelangan tangan, siku, atau bahu) tanpa kekerasan melampau',
      'Menunjukkan kekuatan fizikal otot tangan pesilat semata-mata',
      'Memaksa lawan membayar pampasan kewangan di gelanggang'
    ],
    correctAnswer: 1,
    explanation: 'Kuncian sendi memanfaatkan fizik daya tuas untuk menghentikan keagresifan penyerang secara selamat dan terkawal.'
  },
  {
    id: 62,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah yang dimaksudkan dengan konsep "Langkah Tiga" dalam pergerakan tapak silat?',
    options: [
      'Tiga langkah tarian santai mengikut alunan muzik moden',
      'Pergerakan tiga penjuru langkah tapak strategik yang membentuk segitiga untuk mengelak, memintas, dan mengunci ruang lawan',
      'Berjalan tiga meter ke hadapan dan tiga meter ke belakang',
      'Peraturan melarang melangkah lebih daripada tiga kali dalam satu perlawanan'
    ],
    correctAnswer: 1,
    explanation: 'Langkah Tiga (Tapak Tiga) membentuk geometri taktikal asas untuk mengelilingi zon bahaya lawan dan mencari posisi sudut mati.'
  },
  {
    id: 63,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam teknik "Pentas & Sambut", apakah peranan pesilat yang bertindak sebagai "Penyambut"?',
    options: [
      'Menerima serangan lawan secara pasif tanpa melakukan sebarang tindak balas',
      'Membaca arah serangan, menyerap daya, memesongkan impak, dan melancarkan kuncian atau serangan balas serta-merta',
      'Berteriak sekuat hati untuk menakut-nakutkan pihak lawan',
      'Menyambut tangan lawan untuk bersalam di tengah pertempuran'
    ],
    correctAnswer: 1,
    explanation: 'Penyambut bertindak aktif mengikut prinsip defensif ASMM: menyambut dengan tenang, mematikan serangan, dan mengunci lawan.'
  },
  {
    id: 64,
    category: 'Silat & Kemahiran Silibus',
    question: 'Mengapakah imbangan pernafasan (kawalan nafas) sangat ditekankan semasa melancarkan buah pukulan padu?',
    options: [
      'Supaya pesilat boleh menahan nafas selama sepuluh minit berturut-turut',
      'Menghembus nafas pada detik impak mengetatkan otot teras (core), memaksimakan transmisi tenaga kinetik, dan melindungi organ dalaman',
      'Untuk mengeluarkan bunyi dengkuran yang menggerunkan lawan',
      'Kawal nafas tidak mempunyai sebarang kaitan saintifik dengan impak pukulan'
    ],
    correctAnswer: 1,
    explanation: 'Kordinasi hembusan nafas menyatukan tenaga dalaman, menstabilkan tekanan abdomen, dan melepaskan impak secara padu.'
  },
  {
    id: 65,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam ujian kenaikan bengkung, kriteria "Hormat Perguruan & Adab Murid" lazimnya dinilai berdasarkan:',
    options: [
      'Kecantikan pakaian yang dibeli dari luar negara',
      'Kesempurnaan tatacara hormat masuk gelanggang, salam persilatan, kekemasan pakaian seragam, dan adab terhadap dewan penguji',
      'Ketinggian suara pesilat apabila menjerit di dalam dewan',
      'Jumlah wang saguhati yang diberikan kepada penguji'
    ],
    correctAnswer: 1,
    explanation: 'Hormat perguruan dinilai dari segi adab, ketertiban, salam, disiplin busana, dan penghormatan kepada dewan penguji bertauliah.'
  },
  {
    id: 66,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah sasaran anatomi tubuh yang dilarang sama sekali daripada diserang secara melulu semasa latihan atau ujian di gelanggang?',
    options: [
      'Mata, halkum (leher hadapan), kemaluan, dan bahagian belakang tengkuk tanpa kawalan pelindung',
      'Lengan atas dan bahu luar',
      'Paha bahagian luar',
      'Dada bahagian tengah yang beralas pelindung dada'
    ],
    correctAnswer: 0,
    explanation: 'Titik maut seperti halkum, mata, dan belakang tengkuk boleh menyebabkan kecederaan kekal atau kematian; ia dilarang diserang secara cuai.'
  },
  {
    id: 67,
    category: 'Silat & Kemahiran Silibus',
    question: 'Teknik "Sisip" dalam Seni Silat Awang Sulung Merah Muda merujuk kepada:',
    options: [
      'Menyisip atau menyusupkan tangan ke ruang celah kawalan lawan bagi mematikan gerak dan melancarkan kuncian rapat',
      'Pergerakan tarian moden semasa sesi rehat',
      'Menjatuhkan badan secara berguling di atas tanah tanpa kawalan',
      'Melompat ke belakang untuk melarikan diri dari gelanggang'
    ],
    correctAnswer: 0,
    explanation: 'Teknik sisip memanfaatkan ruang bukaan dan celah serangan musuh untuk menyusup masuk ke posisi rapat serta mengunci sendi lawan.'
  },
  {
    id: 68,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah faedah mempelajari teknik senjata pendek (seperti pisau atau tongkat pendek) dalam silibus lanjutan?',
    options: [
      'Untuk membawa senjata tajam ke mana-mana premis awam',
      'Memahami jarak jangkauan serangan senjata lawan, kaedah menyekat, dan melucutkan senjata musuh dengan selamat',
      'Untuk menunjuk-nunjuk kepada masyarakat bahawa pesilat bersenjata',
      'Supaya pesilat boleh terlibat dalam pertempuran jalanan'
    ],
    correctAnswer: 1,
    explanation: 'Latihan senjata bertujuan melatih refleks, pemahaman zon bahaya (range), dan teknik melucutkan senjata penyerang secara berkesan.'
  },
  {
    id: 69,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah yang dimaksudkan dengan "Serangan Balas Sepantas Kilat" (Kaunter Pantas) dalam silibus tempur ASMM?',
    options: [
      'Menyerang mangsa yang sedang tidur tanpa amaran',
      'Memanfaatkan saat lawan sedang melepaskan daya serangan dan kehilangan imbangan untuk melancarkan penyudah serta-merta',
      'Menggunakan lampu kilat kamera untuk membutakan mata lawan',
      'Melarikan diri dengan kelajuan tinggi'
    ],
    correctAnswer: 1,
    explanation: 'Kaunter pantas memanfaatkan kealpaan pertahanan lawan pada saat serangannya terlajak (over-commitment).'
  },
  {
    id: 70,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam modul seni jurus tari ASMM, apakah peranan alunan muzik gendang silat (Ibu Gendang, Anak Gendang, Serunai)?',
    options: [
      'Hanya sekadar hiasan bunyi tanpa sebarang makna',
      'Membantu pesilat menyelaras ritma langkah, mengawal degupan jantung, melatih ketenangan batin, dan mengekalkan warisan budaya',
      'Membingitkan telinga supaya pesilat hilang tumpuan',
      'Memaksa pesilat melompat tanpa henti'
    ],
    correctAnswer: 1,
    explanation: 'Paluan gendang silat membakar semangat kependekaran di samping melatih ketajaman deria pendengaran dan koordinasi gerak tari.'
  },
  {
    id: 71,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam gerak asas silibus ASMM, apakah perbezaan ketara antara kemahiran "Elakan Luar" dan "Elakan Dalam"?',
    options: [
      'Elakan Luar mengelak ke arah sisi luar garis serangan lawan, manakala Elakan Dalam menyusup ke sisi dalam garis serangan untuk posisi rapat',
      'Elakan Luar menggunakan lompatan tinggi, manakala Elakan Dalam meniarap di atas lantai',
      'Elakan Luar hanya boleh dilakukan di luar gelanggang, manakala Elakan Dalam di dalam gelanggang sahaja',
      'Kedua-dua elakan tersebut langsung tidak memerlukan pergerakan tapak mahupun kekuda'
    ],
    correctAnswer: 0,
    explanation: 'Elakan Luar dan Elakan Dalam melatih ketangkasan pesilat mengelak daripada laluan serangan musuh secara selamat dan membuka peluang serangan balas berkesan.'
  },
  {
    id: 72,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah fungsi pergerakan "Kilek" dalam buah pukulan ASMM?',
    options: [
      'Memotong rumput di sekitar gelanggang silat',
      'Menggunakan sisi luar tapak tangan yang tegar untuk menetak ke arah titik saraf leher atau rusuk lawan',
      'Menampar pipi lawan dengan tapak tangan terbuka',
      'Mengetuk lantai untuk memberi isyarat menyerah kalah'
    ],
    correctAnswer: 1,
    explanation: 'Kilek memfokuskan luas permukaan hentakan yang kecil bagi menghasilkan tekanan impak tinggi pada saraf lawan.'
  },
  {
    id: 73,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam silibus pertahanan diri terhadap rangkulan dari belakang (Bear Hug), tindakan awal yang paling pantas ialah:',
    options: [
      'Menangis dan merayu agar dilepaskan segera',
      'Merendahkan pusat graviti badan (rendah kekuda), menanduk ke belakang atau memijak ibu jari kaki lawan, dan merungkai genggaman tangan musuh',
      'Melompat ke udara setinggi yang boleh',
      'Menyerahkan dompet dan telefon bimbit'
    ],
    correctAnswer: 1,
    explanation: 'Merendahkan pusat graviti menghalang lawan mengangkat pesilat, manakala serangan ke titik lemah (kaki/muka) melonggarkan rangkulan.'
  },
  {
    id: 74,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah maksud "Pecah Langkah" dalam pertempuran berhadapan dengan lebih daripada seorang penyerang (serangan ramai)?',
    options: [
      'Memecahkan cermin mata lawan dengan tumbukan',
      'Membuka kedudukan tapak yang dinamik bagi memastikan pesilat tidak terkepung dan sentiasa menjadikan seorang musuh sebagai penghalang musuh yang lain',
      'Berhenti bergerak dan menunggu giliran dipukul',
      'Berlari mengelilingi bulatan gelanggang tanpa arah'
    ],
    correctAnswer: 1,
    explanation: 'Pecah langkah memanipulasi geometri ruang agar penyerang berbaris satu demi satu dan tidak dapat menyerang serentak dari sudut belakang.'
  },
  {
    id: 75,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam kemahiran bengkung lanjutan, apakah fungsi utama senjata "Tekpi" (Sai/Cabang Tiga)?',
    options: [
      'Alat untuk menggali tanah di kebun',
      'Senjata pertahanan bercabang yang sangat berkesan untuk memerangkap, mengunci, dan mematahkan bilah pedang atau parang musuh',
      'Hanya hiasan dinding rumah pusaka',
      'Senjata lontaran jarak jauh seperti anak panah'
    ],
    correctAnswer: 1,
    explanation: 'Cabang tekpi direka khas untuk menangkap mata bilah senjata panjang lawan dan mematahkan senjata musuh dengan kilasan pergelangan tangan.'
  },
  {
    id: 76,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah fungsi pergerakan "Sapuan Kaki" (Sweep) dalam teknik jatuhan ASMM?',
    options: [
      'Membersihkan habuk di atas lantai gelanggang sebelum latihan',
      'Menyapu kaki tumpuan lawan yang sedang menanggung berat badan untuk menghilangkan imbangan dan merebahkannya ke tanah',
      'Mengelak serangan lawan dengan melompat ke belakang',
      'Menendang bola sepak ke arah gawang'
    ],
    correctAnswer: 1,
    explanation: 'Sapuan kaki mengganggu titik tumpu kestabilan lawan, menyebabkan lawan tumbang serta-merta tanpa memerlukan kudrat yang besar.'
  },
  {
    id: 77,
    category: 'Silat & Kemahiran Silibus',
    question: 'Mengapakah seorang pesilat dilarang memegang bengkung pesilat lain dengan kasar semasa sesi latihan kuncian?',
    options: [
      'Kerana bengkung mudah kotor dan sukar dibasuh',
      'Sebagai tanda memelihara maruah martabat bengkung rakan dan mengelakkan kecederaan tulang pinggul',
      'Kerana bengkung dibuat daripada sutera mahal',
      'Kerana ia dilarang oleh peraturan sukan olahraga'
    ],
    correctAnswer: 1,
    explanation: 'Bengkung adalah lambang tauliah dan kehormatan pesilat; ia dihormati dan tidak dijadikan alat tarikan secara biadab.'
  },
  {
    id: 78,
    category: 'Silat & Kemahiran Silibus',
    question: 'Dalam ujian bertulis dan amali kenaikan bengkung ASMM, markah lulus minima yang ditetapkan lazimnya ialah:',
    options: [
      '40% markah',
      '60% markah keseluruhan',
      '95% markah tanpa sebarang kesilapan',
      '20% markah kehadiran sahaja'
    ],
    correctAnswer: 1,
    explanation: 'Piawaian kelulusan minima 60% memastikan pesilat benar-benar menguasai silibus asas teori dan amali sebelum layak memikul bengkung seterusnya.'
  },
  {
    id: 79,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah yang dimaksudkan dengan "Siku Pancung" dalam serangan jarak rapat?',
    options: [
      'Menghayun siku dari atas ke bawah secara condong ke arah sasaran kepala atau bahu lawan',
      'Menolak lawan dengan tapak tangan lurus',
      'Memegang siku sendiri sambil melompat',
      'Menghulurkan tangan untuk berjabat tangan'
    ],
    correctAnswer: 0,
    explanation: 'Siku pancung memanfaatkan tulang siku yang keras dengan daya gravitasi condong untuk menghasilkan impak remukan pada jarak dekat.'
  },
  {
    id: 80,
    category: 'Silat & Kemahiran Silibus',
    question: 'Apakah langkah keselamatan paling utama sekiranya berlaku kecederaan terseliuh (sprain) pada buku lali semasa sesi latihan?',
    options: [
      'Memaksa pesilat terus berlari untuk menghilangkan rasa sakit',
      'Menghentikan aktiviti serta-merta, merehatkan bahagian tercedera, menuam ais (R.I.C.E), dan merujuk kepada pegawai perubatan bertugas',
      'Menyapu minyak panas mendidih di atas luka',
      'Menyuruh rakan lain memijak buku lali yang bengkak'
    ],
    correctAnswer: 1,
    explanation: 'Prosedur rawatan awal sukan (Rest, Ice, Compression, Elevation) mengurangkan bengkak dan menghalang kerosakan ligamen yang lebih parah.'
  },

  // =========================================================================
  // BAHAGIAN C: SEJARAH & FALSAFAH ASMM (SOALAN 81 - 115)
  // =========================================================================
  {
    id: 81,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Dari manakah asal-usul salasilah ilmu Seni Silat Awang Sulung Merah Muda mula berkembang sebelum diwarisi di Tanah Melayu?',
    options: [
      'Bumi Patani (Siam) — pusat utama tamadun dan penyebaran ilmu seni silat Melayu rantau ini',
      'Kepulauan Hawaii di Lautan Pasifik',
      'Kota metropolitan London di Eropah',
      'Kawasan pergunungan Himalaya di Tibet'
    ],
    correctAnswer: 0,
    explanation: 'Salasilah warisan silat ASMM berakar umbi dari Patani, sebuah pusat keilmuan dan persilatan Melayu agung yang terkenal dengan ketangkasan pendekarnya.'
  },
  {
    id: 82,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Siapakah srikandi yang mendapat ilham gerak tempur kependekaran daripada pemerhatian hening terhadap alam semula jadi di bumi Patani?',
    options: [
      'Srikandi Semerah',
      'Mahsuri dari Langkawi',
      'Che Siti Wan Kembang',
      'Tun Fatimah dari Melaka'
    ],
    correctAnswer: 0,
    explanation: 'Srikandi Semerah mengamati liukan ranting pepohon, riak air sungai, dan tindak balas haiwan rimba sehingga melahirkan gerak silat yang unik.'
  },
  {
    id: 83,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Apakah nama kaedah seni tempur defensif yang dicipta oleh Srikandi Semerah yang menanti serangan dengan tenang dan mematikan langkah lawan?',
    options: [
      'Gerak "Kacak Hati"',
      'Lompatan Helang Menikam',
      'Tumbukan Naga Sakti',
      'Tarian Selendang Sutera'
    ],
    correctAnswer: 0,
    explanation: 'Gerak "Kacak Hati" adalah kaedah saintifik yang menyerap dan membalikkan tenaga lawan dengan tenang tanpa mencederakan secara zalim.'
  },
  {
    id: 84,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Kepada siapakah Srikandi Semerah mengijazahkan ilmu rahsia kependekaran gerak "Kacak Hati"?',
    options: [
      'Adik angkatnya yang berketurunan bangsawan diraja iaitu pemuda bernama Awang Sulung',
      'Hulubalang istana yang tamak kuasa',
      'Saudagar asing yang berniaga di pelabuhan',
      'Orang ramai di pasar secara terbuka'
    ],
    correctAnswer: 0,
    explanation: 'Ilmu rahsia Kacak Hati diamanahkan secara khusus kepada adik angkatnya, Awang Sulung, seorang pemuda berhati bersih dan berbudi pekerti luhur.'
  },
  {
    id: 85,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada usia berapakah Awang Sulung menghadapi ujian kependekaran paling getir apabila dicabar oleh seorang Panglima Raja?',
    options: [
      'Usia 18 Tahun',
      'Usia 40 Tahun',
      'Usia 10 Tahun',
      'Usia 60 Tahun'
    ],
    correctAnswer: 0,
    explanation: 'Ujian paling getir tiba sewaktu Awang Sulung baru berusia 18 tahun, ketika beliau menyandang gelaran "Pendekar Muda".'
  },
  {
    id: 86,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Mengapakah seorang Panglima Raja yang tersohor sombong mencabar Awang Sulung bertarung di hadapan khalayak ramai?',
    options: [
      'Merasa cemburu dengan gelaran Pendekar Muda dan berniat memperlecehkan ilmu yang dipelajari daripada seorang srikandi wanita',
      'Kerana Awang Sulung berhutang wang perniagaan dengannya',
      'Hanya untuk mengadakan persembahan hari raya',
      'Untuk menguji ketajaman senjata baharu'
    ],
    correctAnswer: 0,
    explanation: 'Panglima Raja yang mendabik dada dan berbadan sasa tidak berpuas hati dan mencabar Awang Sulung untuk menjatuhkan maruah ilmunya.'
  },
  {
    id: 87,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Bagaimanakah Awang Sulung berjaya menundukkan kudrat Panglima Raja yang gagah perkasa dalam pertarungan tersebut?',
    options: [
      'Menggunakan tipu helah racun berbisa',
      'Mengikut disiplin gerak Kacak Hati — meliuk santun mengikut arus serangan, mengunci pergelangan sendi, dan merebahkan panglima dalam satu kilasan',
      'Melarikan diri ke dalam hutan sehingga panglima kepenatan',
      'Meminta bantuan tentera istana untuk menangkap panglima'
    ],
    correctAnswer: 1,
    explanation: 'Awang Sulung tidak menyambut kekerasan dengan kekerasan; beliau meliuk mengikut arus serangan dan mengunci sendi sang panglima sebaik hilang imbangan.'
  },
  {
    id: 88,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Apakah tindakan luhur Awang Sulung sejurus selepas merebahkan Panglima Raja yang rebah tersungkur di gelanggang?',
    options: [
      'Bersorak bangga, menghina, dan memijak maruah lawannya',
      'Melutut, menghulurkan kedua-dua belah tangan dengan penuh hormat membantu panglima bangkit, dan memohon kemaafan',
      'Menuntut seluruh harta dan jawatan panglima dirampas',
      'Menghalau panglima tersebut keluar dari negeri'
    ],
    correctAnswer: 1,
    explanation: 'Keluhuran peribadi Awang Sulung terserlah apabila beliau melutut menghulurkan tangan dan memohon maaf, meruntuhkan keegoan sang panglima.'
  },
  {
    id: 89,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Peristiwa kemaafan dan penyatuan antara Awang Sulung dan Panglima Raja telah melahirkan falsafah peribahasa keramat perguruan iaitu:',
    options: [
      '"Biduk Berlalu, Kiambang Bertaut"',
      '"Katak di bawah tempurung"',
      '"Sudah jatuh ditimpa tangga"',
      '"Hangat-hangat tahi ayam"'
    ],
    correctAnswer: 0,
    explanation: 'Pepatah "Biduk Berlalu, Kiambang Bertaut" mengabadikan pengajaran bahawa permusuhan hanyalah perahu melintas seketika; sebaik ia berlalu, persaudaraan bercantum kembali.'
  },
  {
    id: 90,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Apakah makna tersirat di sebalik falsafah agung "Biduk Berlalu, Kiambang Bertaut" dalam kehidupan pesilat ASMM?',
    options: [
      'Pesilat perlu membeli sampan dan biduk untuk mendayung di sungai',
      'Sengketa hanyalah perselisihan sementara; apabila selesai, ukhuwah dan kasih sayang persaudaraan bertaut kembali tanpa meninggalkan parut dendam',
      'Kiambang adalah sejenis tumbuhan air yang berkhasiat tinggi',
      'Semua persengketaan mesti diselesaikan dengan pertarungan di atas perahu'
    ],
    correctAnswer: 1,
    explanation: 'Kemenangan teragung seorang pendekar bukanlah menewaskan lawan, tetapi menundukkan egonya sendiri dan memaafkan demi perpaduan.'
  },
  {
    id: 91,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Mengapakah seni bela diri warisan ini dimasyhurkan dengan nama "Awang Sulung Merah Muda"?',
    options: [
      'Kerana pengasasnya gemar memakai pakaian berwarna merah jambu',
      'Menyatukan keluhuran hati Pendekar Awang Sulung bersama kehebatan Srikandi Semerah (Merah Muda)',
      'Hanya sekadar nama ciptaan tanpa sebarang sejarah',
      'Diambil daripada nama sebuah lagu klasik melayu'
    ],
    correctAnswer: 1,
    explanation: 'Nama "Awang Sulung Merah Muda" mengabadikan gabungan kemuliaan budi Awang Sulung dan kehebatan ilmu kakaknya, Srikandi Semerah.'
  },
  {
    id: 92,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Apakah cogan kata dan pegangan abadi yang menjadi pakaian batin setiap pesilat di bawah naungan panji ASMM?',
    options: [
      '"Silat Itu Menempah Lawan, Pantang Berdamai"',
      '"Silat Itu Mencari Kawan, Bukan Menempah Lawan"',
      '"Kalah Jadi Abu, Menang Jadi Arang"',
      '"Kekuatan Otot Penentu Segala Kejayaan"'
    ],
    correctAnswer: 1,
    explanation: 'Cogan kata "Silat Itu Mencari Kawan, Bukan Menempah Lawan" menegaskan bahawa silat adalah jambatan kasih sayang dan persaudaraan ummah.'
  },
  {
    id: 93,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Siapakah tokoh pengasas yang membawa dan menubuhkan gelanggang awal perguruan ini di Kampung Manjoi, Ipoh, Perak?',
    options: [
      'Allahyarham Tuan Haji Shafie bin Zahid',
      'Hang Tuah dari Melaka',
      'Tok Janggut dari Kelantan',
      'Mat Kilau dari Pahang'
    ],
    correctAnswer: 0,
    explanation: 'Allahyarham Tuan Haji Shafie bin Zahid menuntut ilmu persilatan di Patani sebelum mendirikan gelanggang awal di Kampung Manjoi, Ipoh, Perak.'
  },
  {
    id: 94,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Apakah amanah istana yang pernah disandang oleh Allahyarham Tuan Haji Shafie bin Zahid di Negeri Perak Darul Ridzuan?',
    options: [
      'Pawang Diraja Sultan Perak',
      'Bendahara Negeri Perak',
      'Panglima Perang Selat Melaka',
      'Juragan Kapal Diraja'
    ],
    correctAnswer: 0,
    explanation: 'Allahyarham Tuan Haji Shafie bin Zahid merupakan seorang guru silat tersohor yang pernah memegang amanah sebagai Pawang Diraja Sultan Perak.'
  },
  {
    id: 95,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada tahun 1969, kepada siapakah Allahyarham Tuan Haji Shafie mengijazahkan keseluruhan silibus ilmu persilatan ini?',
    options: [
      'Tuan Guru Azmi bin Abd Hamid, Encik Azli Abd Hamid, dan anak kandung Allahyarham iaitu Encik Azman bin Shafie',
      'Kepada pegawai British di Pulau Pinang',
      'Kepada seluruh penduduk kampung secara rawak',
      'Hanya kepada seorang murid sahaja'
    ],
    correctAnswer: 0,
    explanation: 'Pada tahun 1969, pengijazahan keseluruhan silibus persilatan diserahkan kepada Tuan Guru Azmi bin Abd Hamid, Encik Azli Abd Hamid, dan Encik Azman bin Shafie.'
  },
  {
    id: 96,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada tahun 1983, Tuan Guru Azmi bin Abd Hamid berpindah ke Kedah dan mendirikan gelanggang utama pertama di mana?',
    options: [
      'Kampung Simpang Tiga Keladi, Kulim, Kedah',
      'Pusat Bandar Alor Setar',
      'Pulau Langkawi',
      'Kaki Gunung Jerai'
    ],
    correctAnswer: 0,
    explanation: 'Kampung Simpang Tiga Keladi, Kulim menjadi tapak semaian dan pusat perkembangan utama Silat Awang Sulung Merah Muda di Kedah bermula tahun 1983.'
  },
  {
    id: 97,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Manakah antara berikut BUKAN merupakan gelanggang latihan aktif berdaftar di bawah naungan PSSASMM di Kedah?',
    options: [
      'Gelanggang Pusaka Warisan (Taman Sejahtera, Lunas)',
      'Gelanggang Wadi AnNour (Kulim)',
      'Gelanggang Paya Besar (Kulim)',
      'Gelanggang Helang Laut (Kota London)'
    ],
    correctAnswer: 3,
    explanation: 'Tiga gelanggang warisan utama yang aktif di Kedah ialah Gelanggang Pusaka Warisan, Gelanggang Wadi AnNour, dan Gelanggang Paya Besar.'
  },
  {
    id: 98,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada lambang rasmi PSSASMM, apakah maksud yang dilambangkan oleh "Tulisan Jawi"?',
    options: [
      'Menterjemahkan nama mulia "Awang Sulung" di bahagian atas dan "Merah Muda" di bahagian bawah, melambangkan warisan persuratan tradisi Melayu Islam',
      'Hiasan tulisan moden semata-mata',
      'Nama syarikat percetakan logo',
      'Kod rahsia tentera'
    ],
    correctAnswer: 0,
    explanation: 'Tulisan Jawi menzahirkan identiti persuratan tradisi Melayu Islam dan mengabadikan nama agung Awang Sulung Merah Muda.'
  },
  {
    id: 99,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada lambang rasmi PSSASMM, apakah maksud yang dilambangkan oleh simbol "Kapal Perang Siam"?',
    options: [
      'Pusat salasilah asal-usul persilatan ini yang berasal dan berkembang dari Siam Patani',
      'Aktiviti menangkap ikan laut dalam',
      'Persatuan silat memiliki kapal tentera laut',
      'Hobi belayar jurulatih persatuan'
    ],
    correctAnswer: 0,
    explanation: 'Kapal Perang Siam merakamkan titik sejarah permulaan salasilah silat yang berakar dari Patani sebagai pusat tamadun silat Melayu serantau.'
  },
  {
    id: 100,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada lambang rasmi PSSASMM, apakah falsafah yang dibawa oleh simbol "Lautan Membiru"?',
    options: [
      'Ilmu persilatan, seni tempur fizikal, dan kebatinan yang dituntut tiada noktah penyudahnya — semakin diteroka, semakin luas rahsianya',
      'Kawasan pantai tempat pesilat bermandi-manda',
      'Bahaya tsunami di kawasan lautan',
      'Kewajipan pesilat pandai berenang'
    ],
    correctAnswer: 0,
    explanation: 'Lautan Membiru melambangkan samudera keilmuan yang tidak bertepi; mendidik pesilat sentiasa dahagakan ilmu dan tidak mendabik dada.'
  },
  {
    id: 101,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada lambang rasmi PSSASMM, apakah maksud yang dilambangkan oleh simbol "Tangkai Padi"?',
    options: [
      'Makanan ruji masyarakat desa semata-mata',
      'Menuntut ilmu mengikut resmi padi (makin tunduk makin berisi) serta taat setia kepada raja dan pemerintah negeri',
      'Pekerjaan sampingan ahli persatuan pada musim menuai',
      'Harga beras di pasaran semasa'
    ],
    correctAnswer: 1,
    explanation: 'Tangkai Padi memupuk sifat tawaduk resmi padi, semakin tinggi ilmu semakin merendah diri, serta taat setia kepada raja dan negara.'
  },
  {
    id: 102,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada lambang rasmi PSSASMM, apakah yang dilambangkan oleh simbol "Selendang"?',
    options: [
      'Pakaian fesyen semasa menghadiri majlis perkahwinan',
      'Amanah keilmuan yang dipikul di bahu dipelihara sebaik-baiknya, dan menuntut silat sebagai jihad fisabilillah mempertahankan diri dan maruah',
      'Kain pengelap peluh di gelanggang',
      'Tali pengikat barangan persatuan'
    ],
    correctAnswer: 1,
    explanation: 'Selendang melambangkan amanah persilatan yang digalas di atas bahu pesilat sebagai tanggungjawab jihad membela kebenaran.'
  },
  {
    id: 103,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada lambang rasmi PSSASMM, apakah maksud "Bukit-Bukau Berpuncak Tajam"?',
    options: [
      'Tempat perkhemahan tahunan pesilat di dalam hutan',
      'Ilmu persilatan jika sentiasa dilatih dan diguna akan menjadi semakin tajam, pantas, dan jitu seumpama mata pedang kependekaran',
      'Ketinggian gunung-ganang di negeri Kedah',
      'Laluan sukar untuk mendaki bukit'
    ],
    correctAnswer: 1,
    explanation: 'Puncak tajam bukit melambangkan ketajaman akal, ketangkasan refleks, dan kejituan gerak apabila ilmu sentiasa diasah.'
  },
  {
    id: 104,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Pada lambang rasmi PSSASMM, apakah maksud yang dilambangkan oleh simbol "Awan Putih"?',
    options: [
      'Ramalan cuaca hujan di kawasan gelanggang',
      'Silat sebagai naungan pelindung kepada sesiapa yang memerlukan bantuan, mendidik pesilat berkasih sayang dan menolong sesama insan',
      'Asap daripada pembakaran sampah',
      'Ketinggian tempat terbang kapal terbang'
    ],
    correctAnswer: 1,
    explanation: 'Awan Putih memayungi rasa perlindungan dan kelembutan budi pesilat untuk menaungi masyarakat yang dilanda musibah.'
  },
  {
    id: 105,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Dalam makna warna rasmi PSSASMM, apakah yang dilambangkan oleh Warna Kuning (Emas Diraja)?',
    options: [
      'Awang Sulung berasal daripada titisan keturunan bangsawan diraja berdaulat serta memancarkan kemuliaan budi pekerti',
      'Cahaya lampu jalan raya di waktu malam',
      'Warna seragam sukan sekolah',
      'Warna buah pisang yang telah masak'
    ],
    correctAnswer: 0,
    explanation: 'Warna Kuning Diraja mengangkat kedaulatan bangsa, kemuliaan budi pekerti bangsawan, dan taat setia kepada Raja Melayu.'
  },
  {
    id: 106,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Dalam makna warna rasmi PSSASMM, apakah yang dilambangkan oleh Warna Merah (Darah Pendekar)?',
    options: [
      'Darah kependekaran yang gagah, berani, pantang berundur menegakkan kebenaran dan menentang kezaliman',
      'Tanda amaran kawasan bahaya kebakaran',
      'Warna bunga mawar di taman bunga',
      'Warna cat dinding gelanggang silat'
    ],
    correctAnswer: 0,
    explanation: 'Warna Merah melambangkan semangat waja, keberanian perwira Awang Sulung Merah Muda dalam membela maruah bangsa.'
  },
  {
    id: 107,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Dalam makna warna rasmi PSSASMM, apakah maksud Warna Biru (Lautan Membiru)?',
    options: [
      'Tali perpaduan kukuh dalam kalangan pesilat tanpa mengira darjat, keturunan, mahupun latar belakang — bersatu di bawah satu perguruan',
      'Warna langit cerah di waktu tengah hari',
      'Warna cat kapal nelayan tempatan',
      'Tanda kehausan air semasa latihan'
    ],
    correctAnswer: 0,
    explanation: 'Warna Biru menyatukan seluruh warga pesilat dalam satu ikatan ukhuwah persaudaraan yang utuh tanpa sebarang perbezaan darjat.'
  },
  {
    id: 108,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Dalam makna warna rasmi PSSASMM, apakah yang dilambangkan oleh Warna Putih (Kesucian Hati)?',
    options: [
      'Kesucian hati dan keikhlasan niat; menuntut silat wajib bermula dari jiwa yang bersih, bebas daripada niat sombong, takbur, atau dengki',
      'Kain kafan semata-mata tanpa makna batiniah',
      'Warna tepung beras di dapur',
      'Warna kapur penanda garisan gelanggang'
    ],
    correctAnswer: 0,
    explanation: 'Warna Putih menuntut setiap insan menyucikan rohani daripada sifat mazmumah sebelum melangkah menuntut ilmu persilatan.'
  },
  {
    id: 109,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Dalam makna warna rasmi PSSASMM, apakah yang dilambangkan oleh Warna Hitam (Misteri Pendekar)?',
    options: [
      'Rahsia dan misteri ilmu kependekaran perguruan yang terpelihara rapi, hanya dibuka kepada mereka yang telah terbukti setia dan beradab luhur',
      'Kegelapan malam apabila bekalan elektrik terputus',
      'Kawasan tanah hitam yang subur untuk tanaman',
      'Warna tayar kereta yang digunakan jurulatih'
    ],
    correctAnswer: 0,
    explanation: 'Warna Hitam memelihara amanah rahsia kependekaran agar ilmu pusaka tidak jatuh ke tangan orang yang khianat dan zalim.'
  },
  {
    id: 110,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Apakah tiga teras induk pembangunan yang digariskan dalam pendidikan perguruan ASMM?',
    options: [
      'Teras Pertahanan Diri, Teras Adab & Sahsiah, dan Teras Silibus Rasmi Berperingkat',
      'Teras Perniagaan, Teras Hiburan, dan Teras Pelancongan',
      'Teras Perlumbaan Kereta, Teras Memancing, dan Teras Memasak',
      'Teras Sukan Elektronik, Teras Muzik Rock, dan Teras Media Sosial'
    ],
    correctAnswer: 0,
    explanation: 'Tiga teras induk ASMM menyepadukan kemahiran beladiri fizikal, pembentukan sahsiah rohani, dan sukatan silibus berperingkat.'
  },
  {
    id: 111,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Apabila guru atau jurulatih menegur dan memperbetulkan kekuda atau teknik jurus seorang anak murid, bagaimanakah sikap yang wajar ditunjukkan?',
    options: [
      'Mendengar dengan teliti, menerima teguran dengan hati yang lapang, menunduk hormat, dan segera memperbetulkan kesilapan',
      'Membantah teguran dan mendakwa bahawa gaya peribadinya lebih hebat daripada silibus guru',
      'Berasa merajuk dan terus meninggalkan gelanggang tanpa izin',
      'Menyalahkan rakan latihan di sebelah kononnya menjadi punca kesilapan'
    ],
    correctAnswer: 0,
    explanation: 'Sikap anak murid yang cemerlang sentiasa berlapang dada menerima teguran guru, kerana teguran adalah tanda kasih dan bimbingan untuk menyempurnakan ilmu.'
  },
  {
    id: 112,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Apakah tanggungjawab anak murid terhadap kebersihan gelanggang dan kelengkapan latihan bersama selepas selesai sesi silat?',
    options: [
      'Menyusun semula pad tendangan dan senjata latihan dengan tertib, membersihkan sampah, serta memastikan gelanggang ditinggalkan dalam keadaan bersih dan selamat',
      'Meninggalkan semua peralatan bersepah di atas lantai untuk dikemas oleh guru',
      'Membawa pulang peralatan gelanggang ke rumah tanpa kebenaran jurulatih',
      'Terus pulang ke rumah dengan segera tanpa membantu rakan-rakan membersihkan tempat latihan'
    ],
    correctAnswer: 0,
    explanation: 'Menjaga kebersihan dan mengemas peralatan gelanggang bersama-sama memupuk rasa tanggungjawab, sifat amanah, dan kerendahan diri dalam diri setiap pesilat.'
  },
  {
    id: 113,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Sekiranya rakan segelanggang tercedera ringan atau berasa letih semasa latihan fizikal, apakah tindakan terpuji seorang anak murid silat?',
    options: [
      'Membantu menenangkannya, memberi sokongan semangat, dan memaklumkan segera kepada jurulatih untuk rawatan awal',
      'Mentertawakan kelemahan rakan dan mengejeknya di hadapan pesilat lain',
      'Mengambil peluang untuk terus memukul rakan tersebut agar tewas',
      'Membiarkan sahaja rakan berkenaan tanpa mempedulikan keselamatannya'
    ],
    correctAnswer: 0,
    explanation: 'Semangat persaudaraan (ukhuwah) sesama pesilat mendidik anak murid agar sentiasa prihatin, melindungi, dan membantu rakan segelanggang dalam susah dan senang.'
  },
  {
    id: 114,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Bagi seorang anak murid silat, apakah tujuan sebenar pemakaian bengkung mengikut tingkatan warna (Putih, Kuning, Merah, dan seterusnya)?',
    options: [
      'Sebagai peringatan amanah tahap penguasaan ilmu, kematangan sahsiah diri, dan tanggungjawab membimbing rakan yang lebih muda',
      'Untuk berasa bangga diri dan menakut-nakutkan kawan-kawan di sekolah',
      'Sebagai hiasan fesyen semata-mata tanpa sebarang maksud disiplin',
      'Supaya boleh mencabar orang luar bertarung di tempat awam'
    ],
    correctAnswer: 0,
    explanation: 'Kenaikan bengkung bagi anak murid bukanlah lambang keegoan, sebaliknya tanda bertambahnya amanah, adab, dan tanggungjawab moral dalam perguruan.'
  },
  {
    id: 115,
    category: 'Sejarah & Falsafah ASMM',
    question: 'Bagaimanakah seorang anak murid ASMM dapat membuktikan bahawa ilmu silat yang dipelajarinya telah membentuk sahsiah yang terpuji di luar gelanggang?',
    options: [
      'Menghormati ibu bapa di rumah, tekun belajar pelajaran sekolah, berakhlak mulia kepada jiran, dan suka membantu orang yang memerlukan',
      'Menunjukkan jurus silat di jalanan untuk menakutkan orang ramai',
      'Sering bertengkar dengan adik-beradik kerana merasa dirinya lebih kuat',
      'Enggan mendengar nasihat orang tua kerana menganggap dirinya sudah menjadi pendekar'
    ],
    correctAnswer: 0,
    explanation: 'Kecemerlangan sebenar seorang anak murid silat terpancar melalui akhlak mulia terhadap ibu bapa di rumah, kejayaan dalam pelajaran, dan sikap suka menolong masyarakat.'
  }
];

export const TOTAL_EXAM_QUESTIONS = 50; // Bilangan soalan yang ditanya dalam setiap percubaan (50 soalan rawak)
export const TOTAL_BANK_QUESTIONS = UJIAN_BERTULIS_QUESTIONS.length; // 100 soalan dalam bank soalan
export const ALLOCATED_TIME_MINUTES = 75; // 75 Minit
export const ALLOCATED_TIME_SECONDS = ALLOCATED_TIME_MINUTES * 60; // 4,500 Saat
export const MAX_ALLOWED_ATTEMPTS = 2; // 2 Kali Percubaan
export const PASSING_PERCENTAGE = 60; // 60% markah lulus minima
export const PASSING_SCORE = Math.ceil((TOTAL_EXAM_QUESTIONS * PASSING_PERCENTAGE) / 100); // 30 soalan betul daripada 50

/**
 * Randomly select `count` questions from a question pool (Fisher-Yates Shuffle)
 */
export function getRandomExamQuestions(
  pool: UjianBertulisQuestion[] = UJIAN_BERTULIS_QUESTIONS,
  count = TOTAL_EXAM_QUESTIONS
): UjianBertulisQuestion[] {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, count);
}

export interface UjianBertulisAttempt {
  id: string;
  attemptNumber: 1 | 2;
  userId: string;
  memberId: string;
  userName: string;
  userIcNo?: string;
  gelanggang: string;
  bengkung: string;
  startedAt: string;
  completedAt: string;
  timeSpentSeconds: number;
  score: number; // e.g. 42
  totalQuestions: number; // 50
  percentage: number; // e.g. 84
  isPassed: boolean;
  grade: 'A (Cemerlang)' | 'B (Kepujian)' | 'C (Lulus)' | 'D (Gagal / Mengulang)';
  answers: Record<number, number>; // questionId -> selectedOptionIndex (0-3)
  questionIds?: number[]; // Specific 50 question IDs presented during this attempt
  categoryBreakdown: {
    sahsiah: { correct: number; total: number; percentage: number };
    silat: { correct: number; total: number; percentage: number };
    persatuan: { correct: number; total: number; percentage: number };
  };
}

export function calculateGrade(percentage: number): 'A (Cemerlang)' | 'B (Kepujian)' | 'C (Lulus)' | 'D (Gagal / Mengulang)' {
  if (percentage >= 85) return 'A (Cemerlang)';
  if (percentage >= 70) return 'B (Kepujian)';
  if (percentage >= 60) return 'C (Lulus)';
  return 'D (Gagal / Mengulang)';
}
